// Film Production landing page — monolithic iframe block
// 4 brand variants: noir / obsidian / crimson / volt

export type FilmLandingBrand = 'noir' | 'obsidian' | 'crimson' | 'volt'
export interface FilmLandingPageData { brand: FilmLandingBrand }
export const DEFAULT_FILM_LANDING_DATA: FilmLandingPageData = { brand: 'noir' }

// ── CSS ───────────────────────────────────────────────────────────────────────
const FILM_LANDING_CSS = `
/* ============ БАЗА ============ */
*{margin:0;padding:0;box-sizing:border-box}
html{scroll-behavior:smooth}
body{
  --acc:#e0a83f; --acc-ink:#160d03; --bg:#0b0908; --bg2:#131009;
  --tx:#f0e7d8; --mut:#9a8f7c; --line:rgba(224,168,63,.25); --line2:rgba(240,231,216,.13);
  --fd:'Playfair Display',serif; --imgf:sepia(.22) contrast(1.05); --lsd:0em;
  --bh:clamp(14px,3.4vh,34px); /* высота letterbox-полосы */
  --hh:calc(var(--bh) + 62px); /* суммарная высота шапки с учётом полосы */
  background:var(--bg); color:var(--tx);
  font-family:'Manrope',system-ui,sans-serif; font-size:16px; line-height:1.6;
  overflow-x:hidden; transition:background .6s ease,color .6s ease;
}
body[data-theme="obsidian"]{--acc:#7de3ff;--acc-ink:#04141a;--bg:#05080b;--bg2:#0a1118;--tx:#e8f1f6;--mut:#7e90a2;--line:rgba(125,227,255,.22);--line2:rgba(232,241,246,.12);--fd:'Unbounded',sans-serif;--imgf:saturate(.85) contrast(1.12) hue-rotate(-8deg);--lsd:.01em}
body[data-theme="crimson"]{--acc:#d92446;--acc-ink:#1c040b;--bg:#0a0507;--bg2:#150a0e;--tx:#f6e9ea;--mut:#a2838a;--line:rgba(217,36,70,.28);--line2:rgba(246,233,234,.13);--fd:'Bodoni Moda',serif;--imgf:sepia(.12) saturate(1.15) contrast(1.04);--lsd:.01em}
body[data-theme="volt"]{--acc:#c6ff3a;--acc-ink:#0d1302;--bg:#080907;--bg2:#0e120a;--tx:#eef3e4;--mut:#8d977f;--line:rgba(198,255,58,.25);--line2:rgba(238,243,228,.13);--fd:'Anton',sans-serif;--imgf:saturate(1.1) contrast(1.12);--lsd:.03em}
::selection{background:var(--acc);color:var(--acc-ink)}
::-webkit-scrollbar{width:9px}::-webkit-scrollbar-track{background:var(--bg)}
::-webkit-scrollbar-thumb{background:var(--line);border:2px solid var(--bg)}
a{color:inherit;text-decoration:none}
img{display:block;max-width:100%}
button{font:inherit;color:inherit;background:none;border:0;cursor:pointer}
.mono{font-family:'JetBrains Mono',monospace}
section{scroll-margin-top:calc(var(--hh) + 24px)}
h1,h2,h3,.disp{font-family:var(--fd);letter-spacing:var(--lsd);font-weight:700}

/* ============ ЗЕРНО / КУРСОР / ПРОГРЕСС / ВСПЫШКА ============ */
.grain{position:fixed;inset:-120%;z-index:9980;pointer-events:none;opacity:.08;
  background:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='300' height='300' filter='url(%23n)'/%3E%3C/svg%3E");
  animation:grain 9s steps(10) infinite}
@keyframes grain{0%,100%{transform:translate(0,0)}10%{transform:translate(-4%,-8%)}20%{transform:translate(-9%,3%)}30%{transform:translate(6%,-12%)}40%{transform:translate(-4%,10%)}50%{transform:translate(-9%,4%)}60%{transform:translate(6%,2%)}70%{transform:translate(-2%,-9%)}80%{transform:translate(3%,10%)}90%{transform:translate(-7%,5%)}}
.cur-d,.cur-r{position:fixed;top:0;left:0;border-radius:50%;pointer-events:none;z-index:9999;mix-blend-mode:difference}
.cur-d{width:6px;height:6px;background:#fff}
.cur-r{width:34px;height:34px;border:1px solid #fff;transition:width .35s,height .35s,opacity .3s}
.cur-r.big{width:72px;height:72px}
@media(pointer:coarse){.cur-d,.cur-r{display:none}}
#progress{position:fixed;top:0;left:0;height:2px;width:100%;background:var(--acc);transform:scaleX(0);transform-origin:0 50%;z-index:9990}
#flash{position:fixed;inset:0;background:#000;opacity:0;pointer-events:none;z-index:9985;transition:opacity .18s ease}

/* ============ ЛОАДЕР ============ */
#loader{position:fixed;inset:0;background:#000;z-index:10000;display:flex;align-items:flex-end;justify-content:space-between;padding:5vmin;transition:transform .8s cubic-bezier(.8,0,.2,1)}
#loader.done{transform:translateY(-101%)}
.ld-brand{display:flex;align-items:center;gap:1rem;color:#fff}
.ld-name{font-family:'JetBrains Mono',monospace;font-weight:700;letter-spacing:.35em;font-size:.9rem}
.ld-num{font-family:'JetBrains Mono',monospace;font-size:clamp(2rem,6vw,4rem);color:#fff;font-weight:700}
.ld-bar{position:absolute;left:0;bottom:0;height:2px;background:var(--acc);width:0%}
.emb{width:40px;height:40px;color:currentColor}

/* ============ ШАПКА ============ */
#hdr{position:fixed;top:0;left:0;right:0;z-index:900;display:flex;align-items:center;justify-content:space-between;gap:1rem;
  padding:calc(var(--bh) + .9rem) clamp(1rem,4vw,3rem) 1rem;
  transition:background .4s,backdrop-filter .4s;border-bottom:1px solid transparent}
#hdr.scrolled{background:color-mix(in srgb,var(--bg) 78%,transparent);backdrop-filter:blur(14px);border-bottom-color:var(--line2)}
.hdr-l{display:flex;align-items:center;gap:.9rem}
.hdr-l .emb{color:var(--acc)}
.hdr-tag{font-family:'JetBrains Mono',monospace;font-size:.62rem;letter-spacing:.28em;color:var(--mut);text-transform:uppercase}
.hdr-nav{display:flex;gap:clamp(1rem,2.4vw,2.2rem)}
.hdr-nav a{font-family:'JetBrains Mono',monospace;font-size:.68rem;letter-spacing:.22em;text-transform:uppercase;position:relative;padding:.3rem 0}
.hdr-nav a::after{content:"";position:absolute;left:0;bottom:0;height:1px;width:100%;background:var(--acc);transform:scaleX(0);transform-origin:right;transition:transform .4s cubic-bezier(.2,.7,.2,1)}
.hdr-nav a:hover::after{transform:scaleX(1);transform-origin:left}
.hdr-r{display:flex;align-items:center;gap:1rem}
.hdr-var{font-family:'JetBrains Mono',monospace;font-size:.66rem;letter-spacing:.2em;border:1px solid var(--line);padding:.45rem .8rem;color:var(--acc);white-space:nowrap}
#burger{display:none;flex-direction:column;gap:6px;padding:.6rem .2rem}
#burger i{display:block;width:26px;height:2px;background:var(--tx);transition:transform .4s,opacity .4s}
body.menu-open #burger i:nth-child(1){transform:translateY(4px) rotate(45deg)}
body.menu-open #burger i:nth-child(2){transform:translateY(-4px) rotate(-45deg)}

/* ============ МЕНЮ ============ */
#menu{position:fixed;inset:0;background:var(--bg2);z-index:890;display:flex;flex-direction:column;justify-content:center;padding:calc(var(--bh) + 5rem) clamp(1.4rem,8vw,6rem) 3rem;clip-path:inset(0 0 100% 0);transition:clip-path .7s cubic-bezier(.8,0,.2,1);visibility:hidden}
body.menu-open #menu{clip-path:inset(0 0 0% 0);visibility:visible}
#menu a.ml{font-family:var(--fd);font-size:clamp(2rem,8vw,4.2rem);text-transform:uppercase;line-height:1.25;display:flex;gap:1.2rem;align-items:baseline;overflow:hidden}
#menu a.ml span{display:block;transform:translateY(110%);transition:transform .7s cubic-bezier(.2,.7,.2,1)}
body.menu-open #menu a.ml span{transform:none}
#menu a.ml i{font-style:normal;font-family:'JetBrains Mono',monospace;font-size:.7rem;color:var(--acc)}
#menu a.ml:hover{color:var(--acc)}
.menu-foot{margin-top:3rem;display:flex;justify-content:space-between;gap:1rem;font-family:'JetBrains Mono',monospace;font-size:.66rem;letter-spacing:.2em;color:var(--mut);text-transform:uppercase;flex-wrap:wrap}

/* ============ HERO ============ */
.hero{position:relative;height:100svh;min-height:620px;overflow:hidden}
.hero-imgs{position:absolute;inset:0;will-change:transform}
.hero-imgs img{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;opacity:0;transition:opacity 1.1s ease;transform:scale(1.06)}
.hero-imgs img.on{opacity:1}
.hero-shade{position:absolute;inset:0;background:linear-gradient(to top,var(--bg) 4%,transparent 45%),linear-gradient(to right,rgba(0,0,0,.55),transparent 60%),linear-gradient(to bottom,rgba(0,0,0,.62),transparent 26%),radial-gradient(120% 90% at 50% 10%,transparent 40%,rgba(0,0,0,.55))}
/* letterbox-полосы: тоньше, не перехватывают клики, стоят ниже контента шапки по z-оси */
.bar{position:absolute;left:0;right:0;height:var(--bh);background:#000;z-index:4;pointer-events:none}
.bar-t{top:0;transform-origin:top}
.bar-b{bottom:0;transform-origin:bottom}
body.ready .bar-t{animation:barT 1.2s .1s cubic-bezier(.8,0,.2,1) both}
body.ready .bar-b{animation:barB 1.2s .1s cubic-bezier(.8,0,.2,1) both}
@keyframes barT{from{transform:scaleY(10)}to{transform:scaleY(1)}}
@keyframes barB{from{transform:scaleY(10)}to{transform:scaleY(1)}}
.hero-kicker{position:absolute;top:calc(var(--hh) + 1.4rem);left:clamp(1rem,4vw,3rem);z-index:7;display:flex;align-items:center;gap:.7rem;font-family:'JetBrains Mono',monospace;font-size:.66rem;letter-spacing:.3em;text-transform:uppercase;color:var(--tx);opacity:0;transition:opacity 1s .9s}
body.ready .hero-kicker{opacity:1}
.dot{width:7px;height:7px;border-radius:50%;background:var(--acc);animation:blink 1.6s infinite}
@keyframes blink{50%{opacity:.15}}
.hero-body{position:absolute;left:clamp(1rem,4vw,3rem);right:clamp(1rem,4vw,3rem);bottom:calc(var(--bh) + 4.6rem);z-index:7;display:grid;grid-template-columns:1fr auto;align-items:end;gap:2rem}
.hero h1{font-size:clamp(2.7rem,9.6vw,8.6rem);line-height:.98;text-transform:uppercase;font-weight:900}
.hero h1 .ln{display:block;overflow:hidden;padding-bottom:.06em}
.hero h1 .ln>span{display:block;transform:translateY(112%);transition:transform 1s cubic-bezier(.2,.7,.15,1)}
body.ready .hero h1 .ln>span{transform:none}
.hero h1 .ln:nth-child(2)>span{transition-delay:.12s}
.hero h1 .ln:nth-child(3)>span{transition-delay:.24s}
.hero h1 .ln--o>span{color:transparent;-webkit-text-stroke:1.5px var(--tx)}
.hero-r{max-width:340px;display:flex;flex-direction:column;gap:1.4rem;opacity:0;transform:translateY(20px);transition:opacity .9s 1s,transform .9s 1s}
body.ready .hero-r{opacity:1;transform:none}
.hero-sub{color:var(--mut);font-size:.92rem}
.cta-row{display:flex;gap:.8rem;flex-wrap:wrap}
.btn{display:inline-flex;align-items:center;gap:.7rem;font-family:'JetBrains Mono',monospace;font-size:.68rem;letter-spacing:.22em;text-transform:uppercase;padding:1rem 1.5rem;border:1px solid var(--acc);position:relative;overflow:hidden;transition:color .35s;isolation:isolate}
.btn::before{content:"";position:absolute;inset:0;background:var(--acc);z-index:-1;transform:scaleX(0);transform-origin:right;transition:transform .45s cubic-bezier(.2,.7,.2,1)}
.btn:hover::before{transform:scaleX(1);transform-origin:left}
.btn--p{background:var(--acc);color:var(--acc-ink)}
.btn--p::before{background:var(--tx)}
.btn:hover{color:var(--acc-ink)}
.btn .ar{transition:transform .35s}
.btn:hover .ar{transform:translateX(5px)}
.hero-meta{position:absolute;left:clamp(1rem,4vw,3rem);right:clamp(1rem,4vw,3rem);bottom:calc(var(--bh) + 1.1rem);z-index:7;display:flex;justify-content:space-between;gap:1rem;font-family:'JetBrains Mono',monospace;font-size:.62rem;letter-spacing:.24em;text-transform:uppercase;color:var(--mut)}
.scroll-hint{display:flex;align-items:center;gap:.7rem}
.scroll-hint i{width:1px;height:26px;background:var(--acc);display:block;animation:drop 1.8s cubic-bezier(.6,0,.4,1) infinite;transform-origin:top}
@keyframes drop{0%{transform:scaleY(0)}45%{transform:scaleY(1)}100%{transform:scaleY(0);transform-origin:bottom}}

/* ============ MARQUEE ============ */
.marquee{overflow:hidden;border-block:1px solid var(--line2);padding:1.15rem 0;background:var(--bg)}
.mq-track{display:flex;width:max-content;animation:mq 30s linear infinite}
.mq-track span{white-space:nowrap;font-family:var(--fd);text-transform:uppercase;letter-spacing:.14em;font-size:clamp(.95rem,2vw,1.4rem);color:var(--mut);padding-right:3.5rem}
.marquee.rev .mq-track{animation-direction:reverse}
.marquee.rev .mq-track span{color:transparent;-webkit-text-stroke:1px var(--mut)}
@keyframes mq{to{transform:translateX(-50%)}}

/* ============ СЕКЦИИ ОБЩЕЕ ============ */
.wrap{max-width:1440px;margin:0 auto;padding:clamp(4rem,9vw,8.5rem) clamp(1rem,4vw,3rem)}
.sec-head{display:flex;align-items:baseline;gap:1.4rem;border-bottom:1px solid var(--line2);padding-bottom:1.2rem;margin-bottom:clamp(2.4rem,5vw,4.5rem);flex-wrap:wrap}
.sec-num{font-family:'JetBrains Mono',monospace;color:var(--acc);font-size:.8rem;letter-spacing:.2em}
.sec-head h2{font-size:clamp(1.9rem,5vw,3.8rem);text-transform:uppercase;line-height:1}
.sec-head .ln{display:block;overflow:hidden}
.sec-head .ln>span{display:block;transform:translateY(110%);transition:transform .9s cubic-bezier(.2,.7,.2,1)}
.sec-head.in .ln>span{transform:none}
.sec-head .ln:nth-child(2)>span{transition-delay:.1s}
.sec-note{margin-left:auto;font-family:'JetBrains Mono',monospace;font-size:.62rem;letter-spacing:.22em;color:var(--mut);text-transform:uppercase}
[data-rv]{opacity:0;transform:translateY(34px);transition:opacity .9s ease,transform .9s cubic-bezier(.2,.7,.2,1)}
[data-rv].in{opacity:1;transform:none}

/* ============ РАБОТЫ ============ */
.works{display:grid;grid-template-columns:repeat(12,1fr);gap:clamp(1rem,2.5vw,2.4rem)}
.wk{position:relative}
.wk-a{grid-column:1/8}.wk-b{grid-column:8/13;margin-top:5rem}.wk-c{grid-column:1/6;margin-top:1rem}.wk-d{grid-column:6/13;margin-top:6rem}
.wk-im{display:block;position:relative;overflow:hidden;background:var(--bg2)}
.wk-a .wk-im,.wk-d .wk-im{aspect-ratio:16/10}
.wk-b .wk-im,.wk-c .wk-im{aspect-ratio:4/5}
.wk-im img{width:100%;height:100%;object-fit:cover;filter:var(--imgf);transform:scale(1.02);transition:transform 1s cubic-bezier(.2,.7,.2,1);animation:kb 17s ease-in-out infinite alternate}
@keyframes kb{from{transform:scale(1.02)}to{transform:scale(1.1) translate(1.4%,-1.2%)}}
.wk-im:hover img{transform:scale(1.12)}
.wk-idx{position:absolute;top:1rem;left:1rem;font-family:'JetBrains Mono',monospace;font-size:.66rem;letter-spacing:.2em;color:#fff;background:rgba(0,0,0,.55);padding:.35rem .6rem;backdrop-filter:blur(6px)}
.wk-open{position:absolute;right:1rem;bottom:1rem;font-family:'JetBrains Mono',monospace;font-size:.62rem;letter-spacing:.22em;text-transform:uppercase;color:var(--acc-ink);background:var(--acc);padding:.55rem .9rem;transform:translateY(140%);transition:transform .45s cubic-bezier(.2,.7,.2,1)}
.wk-im:hover .wk-open{transform:none}
.wk-cap{display:flex;justify-content:space-between;align-items:baseline;gap:1rem;padding-top:1rem;border-top:1px solid transparent}
.wk-t{font-size:clamp(1.1rem,2vw,1.5rem);text-transform:uppercase;transition:color .3s}
.wk:hover .wk-t{color:var(--acc)}
.wk-m{color:var(--mut);font-size:.8rem;margin-top:.2rem}
.wk-y{font-family:'JetBrains Mono',monospace;font-size:.7rem;color:var(--mut)}

/* ============ СТУДИЯ ============ */
.studio-grid{display:grid;grid-template-columns:5fr 7fr;gap:clamp(2rem,5vw,5rem)}
.sticky{position:sticky;top:calc(var(--hh) + 30px);align-self:start}
.sticky h2{font-size:clamp(2.2rem,5.4vw,4.4rem);text-transform:uppercase;line-height:1;margin:.6rem 0 1.4rem}
.lead{color:var(--mut);max-width:44ch}
.stats{display:grid;grid-template-columns:1fr 1fr;gap:1px;background:var(--line2);border:1px solid var(--line2);margin-top:2.4rem}
.stat{background:var(--bg);padding:1.4rem 1.2rem}
.stat b{font-family:var(--fd);font-weight:900;font-size:clamp(1.9rem,3.6vw,3rem);color:var(--acc);display:block;line-height:1}
.stat span{font-family:'JetBrains Mono',monospace;font-size:.6rem;letter-spacing:.22em;text-transform:uppercase;color:var(--mut);display:block;margin-top:.5rem}
.studio-txt p{font-size:clamp(1rem,1.5vw,1.2rem);color:var(--tx);margin-bottom:1.4rem;max-width:60ch}
.studio-txt p.big{font-family:var(--fd);font-size:clamp(1.3rem,2.4vw,1.9rem);line-height:1.4;color:var(--tx)}
.studio-txt p.big em{color:var(--acc);font-style:italic}
figure.st-fig{margin:2.6rem 0;overflow:hidden}
.st-fig .fim{overflow:hidden}
.st-fig img{width:100%;aspect-ratio:16/9;object-fit:cover;filter:var(--imgf);animation:kb 19s ease-in-out infinite alternate}
.st-fig figcaption{font-family:'JetBrains Mono',monospace;font-size:.62rem;letter-spacing:.22em;text-transform:uppercase;color:var(--mut);padding-top:.7rem;display:flex;justify-content:space-between}
blockquote.st-q{border-left:2px solid var(--acc);padding-left:clamp(1.2rem,3vw,2.2rem);margin-top:2.6rem}
.st-q p{font-family:var(--fd);font-style:italic;font-size:clamp(1.2rem,2.3vw,1.8rem);line-height:1.45}
.st-q cite{display:block;margin-top:1rem;font-style:normal;font-family:'JetBrains Mono',monospace;font-size:.64rem;letter-spacing:.24em;text-transform:uppercase;color:var(--mut)}

/* ============ УСЛУГИ ============ */
.svc-grid{display:grid;grid-template-columns:5fr 7fr;gap:clamp(2rem,5vw,5rem)}
.svc{border-top:1px solid var(--line2);padding:1.8rem 0;display:grid;grid-template-columns:70px 1fr auto;gap:1.2rem;align-items:baseline;transition:padding-left .4s cubic-bezier(.2,.7,.2,1)}
.svc:last-child{border-bottom:1px solid var(--line2)}
.svc:hover{padding-left:1rem}
.svc i{font-style:normal;font-family:'JetBrains Mono',monospace;font-size:.7rem;color:var(--acc);letter-spacing:.2em}
.svc h3{font-size:clamp(1.2rem,2.4vw,1.8rem);text-transform:uppercase;transition:color .3s}
.svc:hover h3{color:var(--acc)}
.svc p{color:var(--mut);font-size:.85rem;margin-top:.35rem;max-width:52ch}
.svc .go{font-family:'JetBrains Mono',monospace;color:var(--mut);transition:transform .4s,color .3s}
.svc:hover .go{transform:translate(6px,-6px);color:var(--acc)}

/* ============ МАНИФЕСТ ============ */
#manifest .wrap{max-width:1100px;text-align:left}
.mani{font-family:var(--fd);font-size:clamp(1.7rem,4.6vw,3.6rem);line-height:1.22;text-transform:uppercase}
.mani .ln{display:block;overflow:hidden}
.mani .ln>span{display:block;transform:translateY(112%);transition:transform 1s cubic-bezier(.2,.7,.2,1)}
.mani.in .ln>span{transform:none}
.mani .ln:nth-child(2)>span{transition-delay:.12s}
.mani .ln:nth-child(3)>span{transition-delay:.24s}
.mani .accw{color:var(--acc);font-style:italic}
.mani-cta{margin-top:3rem;display:flex;align-items:center;gap:2rem;flex-wrap:wrap}
.mani-cta .rule{flex:1;height:1px;background:var(--line2);min-width:80px}

/* ============ ФУТЕР ============ */
footer{border-top:1px solid var(--line2);background:var(--bg2)}
.f-grid{display:grid;grid-template-columns:1.4fr 1fr 1fr 1fr;gap:clamp(1.6rem,4vw,3rem)}
.f-h{font-family:'JetBrains Mono',monospace;font-size:.62rem;letter-spacing:.26em;text-transform:uppercase;color:var(--acc);margin-bottom:1.1rem}
.f-col a,.f-col p{display:block;color:var(--mut);font-size:.86rem;margin-bottom:.5rem;transition:color .3s}
.f-col a:hover{color:var(--acc)}
.f-idea{font-family:'JetBrains Mono',monospace;font-size:.66rem;letter-spacing:.3em;text-transform:uppercase;color:var(--mut);margin-bottom:1rem}
.f-mail{font-family:var(--fd);font-size:clamp(1.5rem,4.4vw,3.4rem);text-transform:lowercase;position:relative;display:inline-block;line-height:1.1}
.f-mail::after{content:"";position:absolute;left:0;bottom:-4px;height:2px;width:100%;background:var(--acc);transform:scaleX(0);transform-origin:right;transition:transform .5s cubic-bezier(.2,.7,.2,1)}
.f-mail:hover::after{transform:scaleX(1);transform-origin:left}
.f-mail:hover{color:var(--acc)}
.f-mark{font-family:var(--fd);font-weight:900;font-size:clamp(6rem,22vw,20rem);line-height:.85;text-align:center;color:transparent;-webkit-text-stroke:1px var(--line);user-select:none;margin:clamp(2rem,6vw,4rem) 0 0;letter-spacing:.05em}
.f-bottom{display:flex;justify-content:space-between;gap:1rem;flex-wrap:wrap;padding:1.4rem clamp(1rem,4vw,3rem);border-top:1px solid var(--line2);font-family:'JetBrains Mono',monospace;font-size:.6rem;letter-spacing:.22em;text-transform:uppercase;color:var(--mut)}
.f-bottom button{color:var(--acc);letter-spacing:.22em;font-size:.6rem;text-transform:uppercase;font-family:'JetBrains Mono',monospace}

/* ============ ДОК ВАРИАНТОВ ============ */
.dock{position:fixed;left:20px;bottom:20px;z-index:950;display:flex;align-items:center;gap:4px;background:rgba(0,0,0,.6);border:1px solid var(--line2);backdrop-filter:blur(14px);padding:6px}
.dock-l{font-family:'JetBrains Mono',monospace;font-size:.56rem;letter-spacing:.24em;color:var(--mut);text-transform:uppercase;padding:0 .7rem 0 .5rem;border-right:1px solid var(--line2);margin-right:2px}
.dk{font-family:'JetBrains Mono',monospace;font-size:.62rem;letter-spacing:.14em;padding:.6rem .75rem;color:var(--mut);transition:color .3s,background .3s;display:flex;gap:.5rem;align-items:center;white-space:nowrap}
.dk:hover{color:var(--tx)}
.dk.on{background:var(--acc);color:var(--acc-ink)}
.dk b{font-weight:700}

/* ============ АДАПТИВ ============ */
@media(max-width:1024px){
  .hdr-nav{display:none}
  #burger{display:flex}
  .wk-a{grid-column:1/13}.wk-b{grid-column:1/13;margin-top:0}.wk-c{grid-column:1/7}.wk-d{grid-column:7/13;margin-top:0}
  .studio-grid,.svc-grid{grid-template-columns:1fr}
  .sticky{position:static}
}
@media(max-width:760px){
  .hero-body{grid-template-columns:1fr;align-items:end;gap:1.4rem;bottom:calc(var(--bh) + 4.2rem)}
  .hero-r{max-width:100%}
  .wk-c,.wk-d{grid-column:1/13}
  .f-grid{grid-template-columns:1fr 1fr}
  .hdr-tag{display:none}
  .svc{grid-template-columns:44px 1fr auto}
  .dock{left:50%;transform:translateX(-50%);bottom:calc(12px + env(safe-area-inset-bottom))}
  .dock-l{display:none}
  .dk .d-name{display:none}
  .hero-meta .coords{display:none}
}
@media(max-width:480px){
  .f-grid{grid-template-columns:1fr}
  .stats{grid-template-columns:1fr 1fr}
  .hero h1{font-size:13.5vw}
}
@media (prefers-reduced-motion: reduce){
  html{scroll-behavior:auto}
  *,*::before,*::after{animation-duration:.01ms!important;animation-iteration-count:1!important;transition-duration:.01ms!important}
  .grain{animation:none}
  .hero-imgs img,.wk-im img,.st-fig img{animation:none!important}
}
`

// ── HTML body ─────────────────────────────────────────────────────────────────
const FILM_LANDING_HTML_BODY = `
<div id="loader">
  <div class="ld-brand">
    <svg class="emb" viewBox="0 0 44 44"><rect x="1.5" y="1.5" width="41" height="41" fill="none" stroke="currentColor" stroke-width="1.5"/><text x="22" y="27" text-anchor="middle" font-size="12" letter-spacing="1" fill="currentColor" font-family="JetBrains Mono, monospace" font-weight="700">BST</text></svg>
    <span class="ld-name">BST® STUDIO</span>
  </div>
  <div class="ld-num" id="ldNum">000</div>
  <div class="ld-bar" id="ldBar"></div>
</div>

<div class="grain" aria-hidden="true"></div>
<div id="flash" aria-hidden="true"></div>
<div id="progress" aria-hidden="true"></div>
<div class="cur-d" aria-hidden="true"></div>
<div class="cur-r" aria-hidden="true"></div>

<header id="hdr">
  <div class="hdr-l">
    <a href="#top" aria-label="BST — на главную">
      <svg class="emb" viewBox="0 0 44 44"><rect x="1.5" y="1.5" width="41" height="41" fill="none" stroke="currentColor" stroke-width="1.5"/><text x="22" y="27" text-anchor="middle" font-size="12" letter-spacing="1" fill="currentColor" font-family="JetBrains Mono, monospace" font-weight="700">BST</text></svg>
    </a>
    <span class="hdr-tag">независимая студия · осн. 2013</span>
  </div>
  <nav class="hdr-nav">
    <a href="#works">Работы</a><a href="#studio">Студия</a><a href="#services">Ремесло</a><a href="#manifest">Манифест</a><a href="#contact">Контакт</a>
  </nav>
  <div class="hdr-r">
    <div class="hdr-var" id="hdrVar">01 / NOIR</div>
    <button id="burger" aria-label="Меню"><i></i><i></i></button>
  </div>
</header>

<div id="menu">
  <a class="ml" href="#works" style="transition-delay:.05s"><i>01</i><span>Работы</span></a>
  <a class="ml" href="#studio" style="transition-delay:.1s"><i>02</i><span>Студия</span></a>
  <a class="ml" href="#services" style="transition-delay:.15s"><i>03</i><span>Ремесло</span></a>
  <a class="ml" href="#manifest" style="transition-delay:.2s"><i>04</i><span>Манифест</span></a>
  <a class="ml" href="#contact" style="transition-delay:.25s"><i>05</i><span>Контакт</span></a>
  <div class="menu-foot"><span>hello@bst.studio</span><span>работаем по всему миру</span></div>
</div>

<main id="top">
  <!-- HERO -->
  <section class="hero">
    <div class="hero-imgs" id="heroImgs">
      <img class="himg on" data-t="noir" src="https://image.qwenlm.ai/public_source/98a5bd92-7b37-4492-8641-07bfa11d9d5a/169331cf3-b609-40ea-80f9-f3af7a12d30b.png" alt="">
      <img class="himg" data-t="obsidian" src="https://image.qwenlm.ai/public_source/98a5bd92-7b37-4492-8641-07bfa11d9d5a/13c008f07-d07d-4b7f-9091-20573811ad11.png" alt="">
      <img class="himg" data-t="crimson" src="https://image.qwenlm.ai/public_source/98a5bd92-7b37-4492-8641-07bfa11d9d5a/1fb649e0a-5433-4333-a6e0-ada8ced00266.png" alt="">
      <img class="himg" data-t="volt" src="https://image.qwenlm.ai/public_source/98a5bd92-7b37-4492-8641-07bfa11d9d5a/16361a2a1-c061-4f4c-9bf6-5409b780bb8c.png" alt="">
    </div>
    <div class="hero-shade"></div>
    <div class="bar bar-t"></div><div class="bar bar-b"></div>

    <div class="hero-kicker"><span class="dot"></span><span id="kicker">BST STUDIO — КИНО ДЛЯ БРЕНДОВ</span></div>

    <div class="hero-body">
      <h1 aria-label="Заголовок бренда">
        <span class="ln"><span id="tl0">БРЕНДЫ,</span></span>
        <span class="ln ln--o"><span id="tl1">СНЯТЫЕ</span></span>
        <span class="ln"><span id="tl2">КАК КИНО</span></span>
      </h1>
      <div class="hero-r">
        <p class="hero-sub" id="heroSub">Студия кинематографичного брендинга. Свет, сценарий и монтаж смыслов — чтобы о вашей марке говорили шёпотом.</p>
        <div class="cta-row">
          <a class="btn btn--p" href="#works" id="cta1"><span>Смотреть шоурил</span><span class="ar">→</span></a>
          <a class="btn" href="#contact" id="cta2"><span>Обсудить проект</span></a>
        </div>
      </div>
    </div>

    <div class="hero-meta">
      <span class="scroll-hint"><i></i>SCROLL</span>
      <span class="coords">55.75° N — 37.61° E · 35MM / DIGITAL / 4K</span>
      <span id="clock">LOCAL 00:00:00</span>
    </div>
  </section>

  <div class="marquee"><div class="mq-track" id="mq1t"></div></div>

  <!-- РАБОТЫ -->
  <section id="works">
    <div class="wrap">
      <div class="sec-head" data-lines>
        <span class="sec-num">/01</span>
        <h2><span class="ln"><span>Избранные</span></span><span class="ln"><span>работы</span></span></h2>
        <span class="sec-note">BST — SELECTED WORKS · 2019—2025</span>
      </div>
      <div class="works">
        <article class="wk wk-a" data-rv>
          <a class="wk-im" href="#contact" data-hover>
            <img data-img="p1img" src="https://image.qwenlm.ai/public_source/98a5bd92-7b37-4492-8641-07bfa11d9d5a/1a890da91-3676-4cfa-8c49-f861d02f7d8e.png" alt="Кадр из проекта">
            <span class="wk-idx">W—01</span><span class="wk-open">Открыть ↗</span>
          </a>
          <div class="wk-cap"><div><h3 class="wk-t" id="wkT1">Дом «Полночь»</h3><p class="wk-m" id="wkM1">Ювелирный бренд · фильм</p></div><span class="wk-y" id="wkY1">2025</span></div>
        </article>
        <article class="wk wk-b" data-rv>
          <a class="wk-im" href="#contact" data-hover>
            <img data-img="p2img" src="https://image.qwenlm.ai/public_source/98a5bd92-7b37-4492-8641-07bfa11d9d5a/1e3ae8028-0b03-485f-9947-1eb5193d6a25.png" alt="Кадр из проекта">
            <span class="wk-idx">W—02</span><span class="wk-open">Открыть ↗</span>
          </a>
          <div class="wk-cap"><div><h3 class="wk-t" id="wkT2">Аромат «Дым»</h3><p class="wk-m" id="wkM2">Парфюмерия · кампания</p></div><span class="wk-y" id="wkY2">2024</span></div>
        </article>
        <article class="wk wk-c" data-rv>
          <a class="wk-im" href="#contact" data-hover>
            <img data-img="p3img" src="https://image.qwenlm.ai/public_source/98a5bd92-7b37-4492-8641-07bfa11d9d5a/1c9f200dd-f46c-4f66-b1b4-504030a041e2.png" alt="Кадр из проекта">
            <span class="wk-idx">W—03</span><span class="wk-open">Открыть ↗</span>
          </a>
          <div class="wk-cap"><div><h3 class="wk-t" id="wkT3">«Последний сеанс»</h3><p class="wk-m" id="wkM3">Кинотеатр · айдентика</p></div><span class="wk-y" id="wkY3">2024</span></div>
        </article>
        <article class="wk wk-d" data-rv>
          <a class="wk-im" href="#contact" data-hover>
            <img data-img="p4img" src="https://image.qwenlm.ai/public_source/98a5bd92-7b37-4492-8641-07bfa11d9d5a/17ff6aeaa-82cc-4627-9b51-f1f0fab7a205.png" alt="Кадр из проекта">
            <span class="wk-idx">W—04</span><span class="wk-open">Открыть ↗</span>
          </a>
          <div class="wk-cap"><div><h3 class="wk-t" id="wkT4">«Золото цеха»</h3><p class="wk-m" id="wkM4">Мануфактура · ребрендинг</p></div><span class="wk-y" id="wkY4">2023</span></div>
        </article>
      </div>
    </div>
  </section>

  <!-- СТУДИЯ -->
  <section id="studio">
    <div class="wrap studio-grid">
      <div class="sticky">
        <span class="sec-num">/02</span>
        <h2 class="disp">Студия</h2>
        <p class="lead" id="studioLead">Маленькая команда режиссёров, стратегов и дизайнеров. Мы не делаем «контент» — мы ставим сцены, в которых бренд становится героем.</p>
        <div class="stats" id="stats">
          <div class="stat"><b data-n="12">0</b><span>лет практики</span></div>
          <div class="stat"><b data-n="240">0</b><span>проектов</span></div>
          <div class="stat"><b data-n="36">0</b><span>наград</span></div>
          <div class="stat"><b data-n="14">0</b><span>стран</span></div>
        </div>
      </div>
      <div class="studio-txt">
        <p class="big" data-rv data-t="studioText1">Мы собираем бренды как <em>декорации</em>: свет, фактура, пауза. Каждый носитель — кадр, каждый контакт — сцена.</p>
        <p data-rv data-t="studioText2">Внутри BST — режиссёрская группа, дизайн-бюро и продакшн полного цикла. Мы пишем сценарии, ставим свет, снимаем, монтируем и запускаем — от айдентики до премьеры.</p>
        <p data-rv data-t="studioText3">Работаем с домами моды, техно-лабораториями, мануфактурами и уличными марками. Один принцип не меняется: бренд должен переживать моду.</p>
        <figure class="st-fig" data-rv>
          <div class="fim"><img data-img="studioImg" src="https://image.qwenlm.ai/public_source/98a5bd92-7b37-4492-8641-07bfa11d9d5a/1c9f200dd-f46c-4f66-b1b4-504030a041e2.png" alt="Локация BST"></div>
          <figcaption><span data-t="studioCapL">BST — локация / разведка</span><span data-t="studioCapR">кадр 07 / 35mm</span></figcaption>
        </figure>
        <blockquote class="st-q" data-rv>
          <p id="quote">«BST снимает бренды так, что их хочется пересматривать без звука — и всё равно понимать всё»</p>
          <cite id="quoteSrc">— KINOMARKET, 2025</cite>
        </blockquote>
      </div>
    </div>
  </section>

  <!-- УСЛУГИ -->
  <section id="services">
    <div class="wrap svc-grid">
      <div class="sticky">
        <span class="sec-num">/03</span>
        <h2 class="disp" style="font-size:clamp(2.2rem,5.4vw,4.4rem);text-transform:uppercase;line-height:1;margin:.6rem 0 1.4rem">Ремесло</h2>
        <p class="lead">Пять цехов под одной крышей. Никаких субподрядов — свет, камера и код остаются в доме.</p>
      </div>
      <div>
        <div class="svc" data-rv><i>S—01</i><div><h3 data-t="svc1Title">Стратегия и смысл</h3><p data-t="svc1Desc">Позиционирование, платформа бренда, драматургия и сценарии касаний.</p></div><span class="go">↗</span></div>
        <div class="svc" data-rv><i>S—02</i><div><h3 data-t="svc2Title">Айдентика</h3><p data-t="svc2Desc">Эмблема, типографика, системы носителей, гайдлайны как сценарии.</p></div><span class="go">↗</span></div>
        <div class="svc" data-rv><i>S—03</i><div><h3 data-t="svc3Title">Режиссура</h3><p data-t="svc3Desc">Фильмы о бренде, кампании, фэшн-съёмки, клипы и тизеры.</p></div><span class="go">↗</span></div>
        <div class="svc" data-rv><i>S—04</i><div><h3 data-t="svc4Title">Диджитал</h3><p data-t="svc4Desc">Сайты и продукты, 3D, саунд-дизайн и аудио-айдентика.</p></div><span class="go">↗</span></div>
        <div class="svc" data-rv><i>S—05</i><div><h3 data-t="svc5Title">Продакшн</h3><p data-t="svc5Desc">Полный цикл: от раскадрки и кастинга до цвета и премьеры.</p></div><span class="go">↗</span></div>
      </div>
    </div>
  </section>

  <!-- МАНИФЕСТ -->
  <section id="manifest">
    <div class="wrap">
      <div class="sec-head" data-lines>
        <span class="sec-num">/04</span>
        <h2><span class="ln"><span>Манифест</span></span></h2>
        <span class="sec-note">читается вслух · при выключенном свете</span>
      </div>
      <p class="mani" id="mani">
        <span class="ln"><span id="mani0">Мы верим, что бренд — это фильм:</span></span>
        <span class="ln"><span id="mani1">у него есть герой, конфликт и <b class="accw">свет</b>,</span></span>
        <span class="ln"><span id="mani2">в котором его хочется пересматривать.</span></span>
      </p>
      <div class="mani-cta" data-rv>
        <span class="rule"></span>
        <a class="btn btn--p" href="#contact"><span>Поставить свой кадр</span><span class="ar">→</span></a>
      </div>
    </div>
  </section>

  <div class="marquee rev"><div class="mq-track" id="mq2t"></div></div>
</main>

<!-- ФУТЕР -->
<footer id="contact">
  <div class="wrap">
    <div class="sec-head" data-lines>
      <span class="sec-num">/05</span>
      <h2><span class="ln"><span>Контакт</span></span></h2>
      <span class="sec-note">ответ — в течение суток</span>
    </div>
    <div class="f-grid">
      <div class="f-col">
        <p class="f-idea" data-t="footerIdea">Есть идея? Снимем.</p>
        <a class="f-mail" href="mailto:hello@bst.studio" data-hover>hello@bst.studio</a>
      </div>
      <div class="f-col">
        <p class="f-h">Студии</p>
        <p>Москва — Берлин</p><p>Дубай — Сеул</p><p>работаем по всему миру</p>
      </div>
      <div class="f-col">
        <p class="f-h">Навигация</p>
        <a href="#works">Работы</a><a href="#studio">Студия</a><a href="#services">Ремесло</a><a href="#manifest">Манифест</a>
      </div>
      <div class="f-col">
        <p class="f-h">Соцсети</p>
        <a href="#" data-hover>Behance ↗</a><a href="#" data-hover>Vimeo ↗</a><a href="#" data-hover>Instagram ↗</a><a href="#" data-hover>Telegram ↗</a>
      </div>
    </div>
    <div class="f-mark" aria-hidden="true">BST</div>
  </div>
  <div class="f-bottom">
    <span>© 2025 BST® · все кадры защищены</span>
    <span id="fvar">вариант 01 — NOIR</span>
    <span id="fclock">LOCAL 00:00:00</span>
    <button id="toTop" data-hover>наверх ↑</button>
  </div>
</footer>

<!-- ДОК ПЕРЕКЛЮЧЕНИЯ БРЕНДОВ -->
<aside class="dock" role="group" aria-label="Варианты бренда">
  <span class="dock-l">бренд</span>
  <button class="dk on" data-t="noir" data-hover><b>01</b><span class="d-name">NOIR</span></button>
  <button class="dk" data-t="obsidian" data-hover><b>02</b><span class="d-name">OBSIDIAN</span></button>
  <button class="dk" data-t="crimson" data-hover><b>03</b><span class="d-name">CRIMSON</span></button>
  <button class="dk" data-t="volt" data-hover><b>04</b><span class="d-name">VOLT</span></button>
</aside>
`

// ── Engine JS ─────────────────────────────────────────────────────────────────
function filmLandingEngineJs(brand: string, mode: 'canvas' | 'preview'): string {
  return `
(function(){
"use strict";
var RM = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
var FINE = window.matchMedia('(pointer: fine)').matches;
function $(s){return document.querySelector(s)}
function $$(s){return Array.prototype.slice.call(document.querySelectorAll(s))}

/* ---------- ДАННЫЕ БРЕНДОВ ---------- */
var T = {
 noir:{num:'01',name:'NOIR',doc:'BST — NOIR · Кино для брендов',
  kicker:'BST STUDIO — КИНО ДЛЯ БРЕНДОВ',
  title:['БРЕНДЫ,','СНЯТЫЕ','КАК КИНО'],
  sub:'Студия кинематографичного брендинга. Свет, сценарий и монтаж смыслов — чтобы о вашей марке говорили шёпотом.',
  cta1:'Смотреть шоурил',cta2:'Обсудить проект',
  mq1:'SHOWREEL 2025 — КИНО ДЛЯ БРЕНДОВ — СВЕТ · КАДР · СМЫСЛ — ',
  mq2:'AURUM · ДОМ «ПОЛНОЧЬ» · MANUFAKTURA 7 · SEVER · ТЕАТР «ДЫМ» · GOLDEN CAGE · ',
  works:[['Дом «Полночь»','Ювелирный бренд · фильм','2025'],['Аромат «Дым»','Парфюмерия · кампания','2024'],['«Последний сеанс»','Кинотеатр · айдентика','2024'],['«Золото цеха»','Мануфактура · ребрендинг','2023']],
  lead:'Маленькая команда режиссёров, стратегов и дизайнеров. Мы не делаем «контент» — мы ставим сцены, в которых бренд становится героем.',
  mani:['Мы верим, что бренд — это фильм:','у него есть герой, конфликт и <b class="accw">свет</b>,','в котором его хочется пересматривать.'],
  quote:'«BST снимает бренды так, что их хочется пересматривать без звука — и всё равно понимать всё»',qsrc:'— KINOMARKET, 2025'},
 obsidian:{num:'02',name:'OBSIDIAN',doc:'BST — OBSIDIAN · Технологии и звук',
  kicker:'BST LAB — ТЕХНОЛОГИИ И ЗВУК',
  title:['ЗВУК.','ФОРМА.','АБСОЛЮТ.'],
  sub:'Конструкторское бюро цифровых продуктов. Точность инженерии и чистота визуального языка — до миллисекунды.',
  cta1:'Смотреть шоурил',cta2:'Обсудить проект',
  mq1:'SHOWREEL 2025 — ЦИФРОВЫЕ ПРОДУКТЫ — ТОЧНОСТЬ · ЗВУК · ФОРМА — ',
  mq2:'ONYX AUDIO · IMPULSE MOTORS · KOD25 · SILENCE LAB · NORD BANK · HELIOS EV · ',
  works:[['Onyx','Смарт-колонка · запуск','2025'],['Silence','Шумоподавление · фильм','2024'],['Impulse 9','Электрокар · кампания','2024'],['Kod 25','Финтех · айдентика','2023']],
  lead:'Инженеры, дизайнеры и саунд-архитекторы. Мы проектируем продукты, у которых нет лишних деталей — только функция и форма.',
  mani:['Мы верим в <b class="accw">точность</b>:','красота — это инженерия,','доведённая до тишины.'],
  quote:'«BST проектирует интерфейсы, как часовщики собирают механизм, — без единой лишней детали»',qsrc:'— TECHFRAME, 2025'},
 crimson:{num:'03',name:'CRIMSON',doc:'BST — CRIMSON · Модный дом',
  kicker:'BST MAISON — КАМПАНИИ И ПОКАЗЫ',
  title:['КРАСНЫЙ.','ЧЁРНЫЙ.','ВЕЧНЫЙ.'],
  sub:'Креативная дирекция для модных домов: кампании, лукбуки и показы, снятые как искусство.',
  cta1:'Смотреть лукбук',cta2:'Заказать съёмку',
  mq1:'COLLECTION FW25 — ВЫСОКАЯ МОДА — КРАСНЫЙ · ЧЁРНЫЙ · ВЕЧНЫЙ — ',
  mq2:'MAISON ROUGE · VELVET · KORSET · GRANAT · ATELIER 9 · L\'ÉCLAT · ',
  works:[['Velvet','Коллекция FW25 · фильм','2025'],['Алый след','Аромат · кампания','2024'],['Korset','Кутюр · лукбук','2024'],['Гранат','Ювелирная линия · запуск','2023']],
  lead:'Дом, в котором работают режиссёры, стилисты и фотографы. Мы одеваем бренды в ткань, свет и жест.',
  mani:['Мы верим в <b class="accw">красоту</b>,','от которой перехватывает дыхание:','мода проходит — кадр остаётся.'],
  quote:'«Каждый их кадр — обложка. BST работает с тканью, как режиссёр со светом»',qsrc:'— MODE MAGAZINE, 2025'},
 volt:{num:'04',name:'VOLT',doc:'BST — VOLT · Стрит и энергия',
  kicker:'BST CREW — ДРОПЫ, КАМПАНИИ, СОБЫТИЯ',
  title:['ЭНЕРГИЯ','БЕЗ','ПРАВИЛ'],
  sub:'Собираем дропы, кампании и события для брендов, которые живут на скорости. Громко, быстро, точно.',
  cta1:'Смотреть дроп',cta2:'Зажечь проект',
  mq1:'DROP 2025 — БЕЗ ПРАВИЛ — ЭНЕРГИЯ · СКОРОСТЬ · БЕТОН — ',
  mq2:'VOLTWEAR · NIGHT RACE · STATIKA · RAZRYAD FEST · BUNKER · SPRINT 9 · ',
  works:[['Высокое напряжение','Дроп кроссовок · фильм','2025'],['Ночь трассы','Мото-кампания','2024'],['Статика','Скейт-фильм','2024'],['Разряд','Фестиваль · айдентика','2023']],
  lead:'Крю, а не офис: режиссёры, райдеры, дизайнеры. Мы делаем то, что город замечает за секунду и запоминает на месяц.',
  mani:['Мы верим в <b class="accw">скорость</b>:','лучшая идея — та,','что уже запущена.'],
  quote:'«BST делает так, что от брифа до дропа проходит три недели, а город уже в их постерах»',qsrc:'— STREETVOICE, 2025'}
};
var ORDER = ['noir','obsidian','crimson','volt'];
var cur = 'noir';

/* ---------- SCRAMBLE ---------- */
var CH = 'BST#/КХ▮01';
function scramble(el, txt, dur){
  if(!el) return;
  if(RM){ el.textContent = txt; return; }
  dur = dur || 750;
  var t0 = performance.now(), n = txt.length;
  (function f(t){
    var p = Math.min(1,(t-t0)/dur), s='';
    for(var i=0;i<n;i++){
      var c = txt[i];
      if(c===' '){s+=' ';continue}
      s += (i < p*n) ? c : CH[(Math.random()*CH.length)|0];
    }
    el.textContent = s;
    if(p<1) requestAnimationFrame(f); else el.textContent = txt;
  })(t0);
}

/* ---------- MARQUEE ---------- */
function fillMq(el, text){
  if(!el) return;
  var rep = Math.max(2, Math.ceil(160 / Math.max(20,text.length)) + 2);
  var half = '';
  for(var i=0;i<rep;i++) half += text;
  el.innerHTML = '<span>'+half+'</span><span>'+half+'</span>';
}

/* ---------- СМЕНА ТЕМЫ ---------- */
var flash = $('#flash');
function applyCopy(id){
  var d = T[id];
  document.body.dataset.theme = id;
  document.title = d.doc;
  scramble($('#kicker'), d.kicker, 600);
  scramble($('#tl0'), d.title[0]); scramble($('#tl1'), d.title[1]); scramble($('#tl2'), d.title[2]);
  $('#heroSub').textContent = d.sub;
  $('#cta1').firstElementChild.textContent = d.cta1;
  $('#cta2').firstElementChild.textContent = d.cta2;
  $('#hdrVar').textContent = d.num + ' / ' + d.name;
  $('#fvar').textContent = 'вариант ' + d.num + ' — ' + d.name;
  $('#studioLead').textContent = d.lead;
  $('#quote').textContent = d.quote;
  $('#quoteSrc').textContent = d.qsrc;
  $('#mani0').innerHTML = d.mani[0]; $('#mani1').innerHTML = d.mani[1]; $('#mani2').innerHTML = d.mani[2];
  for(var i=0;i<4;i++){
    $('#wkT'+(i+1)).textContent = d.works[i][0];
    $('#wkM'+(i+1)).textContent = d.works[i][1];
    $('#wkY'+(i+1)).textContent = d.works[i][2];
  }
  fillMq($('#mq1t'), d.mq1); fillMq($('#mq2t'), d.mq2);
  $$('.himg').forEach(function(im){ im.classList.toggle('on', im.dataset.t === id); });
  $$('.dk').forEach(function(b){ b.classList.toggle('on', b.dataset.t === id); });
}
function setTheme(id){
  if(id === cur || !T[id]) return;
  cur = id;
  if(RM){ applyCopy(id); return; }
  flash.style.opacity = 1;
  setTimeout(function(){
    applyCopy(id);
    setTimeout(function(){ flash.style.opacity = 0; }, 120);
  }, 190);
}
$$('.dk').forEach(function(b){ b.addEventListener('click', function(){ setTheme(b.dataset.t); }); });
document.addEventListener('keydown', function(e){
  var k = parseInt(e.key,10);
  if(k>=1 && k<=4) setTheme(ORDER[k-1]);
});

/* ---------- ЛОАДЕР ---------- */
var loader = $('#loader'), ldNum = $('#ldNum'), ldBar = $('#ldBar');
function finishLoad(){
  loader.classList.add('done');
  document.body.classList.add('ready');
  setTimeout(function(){ loader.style.display='none'; }, 900);
  var d = T[cur];
  scramble($('#tl0'), d.title[0], 900); scramble($('#tl1'), d.title[1], 900); scramble($('#tl2'), d.title[2], 900);
}
if(RM){ finishLoad(); }
else{
  var p = 0, t0 = performance.now();
  (function tick(t){
    p = Math.min(100, Math.round((t - t0)/9));
    ldNum.textContent = String(p).padStart(3,'0');
    ldBar.style.width = p + '%';
    if(p<100) requestAnimationFrame(tick);
    else setTimeout(finishLoad, 220);
  })(t0);
}

/* ---------- REVEAL ---------- */
var io = new IntersectionObserver(function(es){
  es.forEach(function(e){ if(e.isIntersecting){ e.target.classList.add('in'); io.unobserve(e.target); } });
},{threshold:.15});
$$('[data-rv],[data-lines],.mani').forEach(function(el){ io.observe(el); });

/* ---------- СЧЁТЧИКИ ---------- */
var cio = new IntersectionObserver(function(es){
  es.forEach(function(e){
    if(!e.isIntersecting) return;
    cio.unobserve(e.target);
    $$('#stats b').forEach(function(b){
      var n = +b.dataset.n;
      if(RM){ b.textContent = n; return; }
      var s = performance.now();
      (function f(t){
        var q = Math.min(1,(t-s)/1500), ease = 1-Math.pow(1-q,3);
        b.textContent = Math.round(n*ease);
        if(q<1) requestAnimationFrame(f);
      })(s);
    });
  });
},{threshold:.4});
cio.observe($('#stats'));

/* ---------- СКРОЛЛ: ПРОГРЕСС / ШАПКА / ПАРАЛЛАКС ---------- */
var hdr = $('#hdr'), prog = $('#progress'), heroImgs = $('#heroImgs');
var mx=0,my=0,px=0,py=0;
window.addEventListener('scroll', function(){
  var y = window.scrollY;
  hdr.classList.toggle('scrolled', y > 40);
  var h = document.documentElement.scrollHeight - innerHeight;
  prog.style.transform = 'scaleX(' + (h>0 ? y/h : 0) + ')';
  if(!RM && y < innerHeight) heroImgs.style.transform = 'translate3d(' + (px*12) + 'px,' + (y*0.08 + py*8) + 'px,0)';
},{passive:true});
if(FINE && !RM){
  window.addEventListener('mousemove', function(e){
    mx = (e.clientX/innerWidth - .5); my = (e.clientY/innerHeight - .5);
  },{passive:true});
}

/* ---------- КУРСОР ---------- */
var cd = $('.cur-d'), cr = $('.cur-r');
if(FINE && !RM){
  var rx=innerWidth/2, ry=innerHeight/2, dx=rx, dy=ry;
  document.addEventListener('mousemove', function(e){ dx=e.clientX; dy=e.clientY; },{passive:true});
  (function loop(){
    rx += (dx-rx)*.16; ry += (dy-ry)*.16;
    cd.style.transform = 'translate(' + (dx-3) + 'px,' + (dy-3) + 'px)';
    cr.style.transform = 'translate(' + (rx-cr.offsetWidth/2) + 'px,' + (ry-cr.offsetHeight/2) + 'px)';
    requestAnimationFrame(loop);
  })();
  $$('a,button,[data-hover]').forEach(function(el){
    el.addEventListener('mouseenter', function(){ cr.classList.add('big'); });
    el.addEventListener('mouseleave', function(){ cr.classList.remove('big'); });
  });
}else{ cd.style.display='none'; cr.style.display='none'; }

/* ---------- ЧАСЫ ---------- */
function tickClock(){
  var d = new Date();
  var s = 'LOCAL ' + String(d.getHours()).padStart(2,'0') + ':' + String(d.getMinutes()).padStart(2,'0') + ':' + String(d.getSeconds()).padStart(2,'0');
  $('#clock').textContent = s; $('#fclock').textContent = s;
}
tickClock(); setInterval(tickClock, 1000);

/* ---------- МЕНЮ ---------- */
$('#burger').addEventListener('click', function(){ document.body.classList.toggle('menu-open'); });
$$('#menu a').forEach(function(a){ a.addEventListener('click', function(){ document.body.classList.remove('menu-open'); }); });

/* ---------- НАВЕРХ ---------- */
$('#toTop').addEventListener('click', function(){ window.scrollTo({top:0, behavior: RM ? 'auto' : 'smooth'}); });

/* ---------- СТАРТ ---------- */
applyCopy('${brand}');
})();
${mode === 'canvas' ? `(function(){
  document.querySelectorAll('[data-rv],.rv,.reveal').forEach(function(el){el.classList.add('in');});
  document.querySelectorAll('.ln').forEach(function(el){var i=el.querySelector('.ln-inner')||el;i.style.transform='none';i.style.opacity='1';});
  document.body.classList.add('loaded','is-ready','ready');
  document.addEventListener('click',function(e){var a=e.target.closest('a[href]');if(a)e.preventDefault();},true);
  document.querySelectorAll('form').forEach(function(f){f.addEventListener('submit',function(e){e.preventDefault();});});
})();` : ''}
`
}

// ── Public API ────────────────────────────────────────────────────────────────
export function getFilmLandingPageHtml(data: FilmLandingPageData, mode: 'canvas' | 'preview'): string {
  const brand = data.brand ?? 'noir'
  return `<!DOCTYPE html>
<html lang="ru">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0, viewport-fit=cover">
<title>BST — NOIR · Кино для брендов</title>
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/playfair-display@5/400.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/playfair-display@5/700.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/playfair-display@5/900.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/playfair-display@5/400-italic.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/unbounded@5/400.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/unbounded@5/700.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/unbounded@5/900.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/bodoni-moda@5/400.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/bodoni-moda@5/700.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/bodoni-moda@5/400-italic.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/anton@5/400.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/manrope@5/400.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/manrope@5/500.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/manrope@5/700.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/jetbrains-mono@5/400.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/jetbrains-mono@5/700.css">
<style>${FILM_LANDING_CSS}</style>
</head>
<body data-theme="${brand}">
${FILM_LANDING_HTML_BODY}
<script>${filmLandingEngineJs(brand, mode)}</script>
</body>
</html>`
}
